<template>
  <div>
    <div class="align-items-center justify-content-center d-md-flex m-5">
      <b-card class="px-1 m-0" style="border-radius: 20px; border: 1px solid lightgrey; max-width: 900px; width: 100%;">
        <div class="mt-1 text-center">
          <h3>Signal Sure Agent AI</h3>
        </div>

        <b-card-title title-tag="h2" class="font-weight-bolder mb-50 mt-2 text-center">
          Choose Your Subscription Plan
        </b-card-title>

        <b-card-text class="text-center text-muted mb-3">
          Select a plan to get started with Signal Sure Agent AI
        </b-card-text>

        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary" label="Loading..." />
        </div>

        <div v-else-if="plans.length === 0" class="text-center py-5">
          <p class="text-muted">No subscription plans available at the moment.</p>
        </div>

        <b-row v-else>
          <b-col v-for="plan in plans" :key="plan.id" md="4" class="mb-3">
            <b-card
              class="h-100 subscription-card"
              style="cursor: default; border: 2px solid transparent;"
            >
              <b-card-title class="text-center font-weight-bolder">
                {{ plan.name }}
              </b-card-title>
              <b-card-text class="text-center">
                <h2 class="font-weight-bolder text-primary">
                  ${{ plan.price }}
                  <small class="text-muted font-weight-normal">/month</small>
                </h2>
              </b-card-text>
              <b-card-text>
                <ul class="list-unstyled mb-0">
                  <li class="mb-2">
                    <feather-icon icon="CheckCircleIcon" class="text-success mr-1" />
                    {{ plan.lead_limit }} leads per month
                  </li>
                  <li v-if="plan.features" v-for="feature in parseFeatures(plan.features)" :key="feature" class="mb-2">
                    <feather-icon icon="CheckCircleIcon" class="text-success mr-1" />
                    {{ feature }}
                  </li>
                </ul>
              </b-card-text>
              <b-button
                block
                variant="primary"
                class="mt-3"
                @click="selectPlan(plan.id)"
                :disabled="isSubmitting && selectedPlanId === plan.id"
              >
                <b-spinner v-if="isSubmitting && selectedPlanId === plan.id" small />
                <span v-else>Select Package</span>
              </b-button>
            </b-card>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  computed: {
    ...mapGetters("appData", ["getLoggedInUser"]),
  },
  data() {
    return {
      plans: [],
      selectedPlanId: null,
      isLoading: false,
      isSubmitting: false,
    };
  },

  created() {
    console.log("PublicSubscription component mounted");
    this.fetchPlans();
  },

  methods: {
    ...mapActions("appData", [
      "getSubscriptionPlans",
      "createAgentSubscription",
    ]),

    async fetchPlans() {
      try {
        this.isLoading = true;
        const res = await this.getSubscriptionPlans({ page: 1 });
        this.plans = res.data.results || [];
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to load subscription plans",
              icon: "XCircleIcon",
              variant: "danger",
            },
          },
          { position: "top-right" }
        );
      } finally {
        this.isLoading = false;
      }
    },

    selectPlan(planId) {
      this.selectedPlanId = planId;
      this.subscribeToPlan(planId);
    },

    async subscribeToPlan(planId) {
      this.isSubmitting = true;

      try {
        const payload = {
          subscription_plan: planId,
          agent: this.getLoggedInUser?.id,
        };

        const res = await this.createAgentSubscription(payload);

        if (res.status === 201 || res.status === 200) {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "Subscription Successful",
                text: "Your subscription has been submitted. Please log in again to continue.",
                icon: "CheckCircleIcon",
                variant: "success",
              },
            },
            { position: "top-right" }
          );

          // Redirect to login after 2 seconds
          setTimeout(() => {
            this.$router.push({ name: "Login" });
          }, 2000);
        }
      } catch (error) {
        console.error("Error submitting subscription:", error);
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to submit subscription request",
              icon: "XCircleIcon",
              variant: "danger",
            },
          },
          { position: "top-right" }
        );
      } finally {
        this.isSubmitting = false;
        this.selectedPlanId = null;
      }
    },

    parseFeatures(features) {
      if (!features) return [];
      if (Array.isArray(features)) return features;
      if (typeof features === 'string') {
        try {
          return JSON.parse(features);
        } catch {
          return features.split(',').map(f => f.trim());
        }
      }
      return [];
    },
  },
};
</script>

<style scoped>
.subscription-card {
  transition: all 0.3s ease;
}

.subscription-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.selected-plan {
  border-color: #00a8cc !important;
  background-color: #f0fbfc;
}
</style>
